import { useState } from "react";
import type { WorkMedia } from "@yushi-portfolio/content-schema";
import type { ApiClient } from "../lib/api";
import { useAssets } from "../lib/assets";
import { emptyMedia, type MediaForm } from "../lib/forms";
import { AssetPicker, AssetThumb, assetLabel } from "./AssetUI";
import { LocalizedInput, move, RowControls, Select } from "./fields";

const KINDS: { value: WorkMedia["kind"]; label: string }[] = [
  { value: "artwork", label: "artwork 作品" },
  { value: "detail", label: "detail 部分" },
  { value: "documentation", label: "documentation 記録" },
  { value: "installation", label: "installation 展示風景" },
  { value: "video-still", label: "video-still スチル" },
  { value: "video", label: "video 映像" },
];

/**
 * Ordered media references. Order and semantic kind only — layout is decided
 * by the public site.
 */
export function MediaListEditor({
  api,
  media,
  onChange,
  allowFeatured = true,
}: {
  api: ApiClient;
  media: MediaForm[];
  onChange: (m: MediaForm[]) => void;
  allowFeatured?: boolean;
}) {
  const { byId } = useAssets();
  /** Row index being picked for, or "new" to append. */
  const [picking, setPicking] = useState<number | "new">();
  const update = (index: number, patch: Partial<MediaForm>) => onChange(media.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const setFeatured = (index: number, featured: boolean) => onChange(media.map((m, i) => ({ ...m, featured: i === index ? featured : false })));

  return (
    <div className="stack">
      {media.length === 0 && <p className="muted">メディアはまだありません。</p>}
      {media.map((m, index) => (
        <div className="card-row" key={m.key}>
          <div className="row-head">
            <strong>#{index + 1}</strong>
            <RowControls index={index} count={media.length} onMove={(f, t) => onChange(move(media, f, t))} onRemove={() => onChange(media.filter((_, i) => i !== index))} />
          </div>
          <div className="media-pick">
            <AssetThumb asset={byId.get(m.assetId)} />
            <div className="stack-tight grow">
              <span>{m.assetId ? assetLabel(byId.get(m.assetId), m.assetId) : "未選択"}</span>
              <button type="button" onClick={() => setPicking(index)}>
                {m.assetId ? "差し替え…" : "選択…"}
              </button>
            </div>
            <Select label="種類" value={m.kind} options={KINDS} onChange={(kind) => update(index, { kind })} />
          </div>
          {allowFeatured && (
            <label className="checkbox">
              <input type="checkbox" checked={m.featured} onChange={(e) => setFeatured(index, e.target.checked)} />
              代表メディア(一覧・ページ冒頭に使われる)
            </label>
          )}
          <LocalizedInput label="代替テキスト (alt)" value={m.alt} onChange={(alt) => update(index, { alt })} />
          <LocalizedInput label="キャプション" value={m.caption} onChange={(caption) => update(index, { caption })} />
        </div>
      ))}
      <div>
        <button type="button" onClick={() => setPicking("new")}>
          + メディアを追加
        </button>
      </div>
      {picking !== undefined && (
        <AssetPicker
          api={api}
          onClose={() => setPicking(undefined)}
          onSelect={(asset) => {
            const kind: WorkMedia["kind"] = asset.type === "video" ? "video" : "artwork";
            if (picking === "new") onChange([...media, { ...emptyMedia(), assetId: asset.id, kind }]);
            else update(picking, { assetId: asset.id });
            setPicking(undefined);
          }}
        />
      )}
    </div>
  );
}
